import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateProductInput,
  Product,
  ProductsListResponse,
  UpdateProductInput,
} from '@/features/catalog/types/product.types';
import { apiFetch } from '@/lib/api';

/**
 * Catalog products — TanStack Query hooks.
 *
 * THE critical Slice 1 endpoint — products carry the
 * `defaultCost` field that is FIELD-GATED by role (see
 * `apps/api/src/resources/ProductResource.ts`). The wire
 * payload for WAREHOUSE OMITS `defaultCost` entirely; the
 * `Product` type in `product.types.ts` already makes it
 * optional. Pages must narrow with `'defaultCost' in product`
 * (or by reading `user.role === 'WAREHOUSE'`) before
 * rendering the column.
 *
 * Four FK filters are exposed (`brandId`, `productModelId`,
 * `productTypeId`, `currencyId`) — they AND-combine into the
 * `WHERE` clause per `apps/api/src/routes/products.ts`.
 */

export type ProductsSortKey = 'name' | 'sku';
export type SortDirection = 'asc' | 'desc';

export interface ProductsListParams {
  page: number;
  pageSize: number;
  sort?: ProductsSortKey;
  dir?: SortDirection;
  search?: string;
  isActive?: 'active' | 'inactive' | 'all';
  brandId?: string;
  productModelId?: string;
  productTypeId?: string;
  currencyId?: string;
}

function buildQuery(params: ProductsListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.search) sp.set('search', params.search);
  if (params.brandId) sp.set('brandId', params.brandId);
  if (params.productModelId) sp.set('productModelId', params.productModelId);
  if (params.productTypeId) sp.set('productTypeId', params.productTypeId);
  if (params.currencyId) sp.set('currencyId', params.currencyId);
  if (params.isActive === 'inactive') sp.set('isActive', 'false');
  if (params.isActive === 'all') sp.set('includeInactive', 'true');
  return sp.toString();
}

export const productsKeys = {
  all: ['products'] as const,
  list: (params: ProductsListParams) => ['products', 'list', params] as const,
  detail: (id: string) => ['products', 'detail', id] as const,
};

export function useProducts(params: ProductsListParams) {
  return useQuery({
    queryKey: productsKeys.list(params),
    queryFn: () => apiFetch<ProductsListResponse>(`/products?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useProduct(id: string | null) {
  return useQuery({
    queryKey: productsKeys.detail(id ?? ''),
    queryFn: () => apiFetch<Product>(`/products/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) =>
      apiFetch<Product>('/products', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: productsKeys.all }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProductInput }) =>
      apiFetch<Product>(`/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: productsKeys.all });
      qc.invalidateQueries({ queryKey: productsKeys.detail(id) });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; id: string }>(`/products/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: productsKeys.all }),
  });
}
